import { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap';
import { Chip } from '@mui/material';
import Avatar from 'react-avatar';
import { useQuery, useMutation } from '@tanstack/react-query';
import Icon from '@mdi/react';
import { mdiAccountOutline } from '@mdi/js';
import { toast } from 'react-toastify';
import './Subject.scss';
import expand from '../../../../assets/images/calibration/expand.png';
import collapse from '../../../../assets/images/calibration/collapse.png';
import {
  AsyncAutocompleteSelect,
  UserAsyncAutocompleteSelect,
} from '../../../../components/Common';
import {
  fetchClassification,
  fetchDelegateApprovalAutoComplete,
  fetchEmployeeLocation,
} from '../../../../store/apis/autocompleteService';
import { getAuthUser } from '../../../../utils/auth.util';
import { fetchEmployeeList } from '../../../../store/apis/employeeService';
import { ImageFileUtil } from '../../../../utils';
import { fetchHRCodeList } from '../../../../store/apis/commonService';

function Subject({
  isOpen,
  toggle,
  togglePeople,
  initialSelectedSubjects,
  peoples = [],
}) {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const { companyList } = getAuthUser();
  const [companyCode, setCompanyCode] = useState(null);
  const [department, setDepartment] = useState(null);
  const [location, setLocation] = useState(null);
  const [manager, setManager] = useState(null);
  const [classification, setClassification] = useState(null);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    setEmployees([]);
    if (!initialSelectedSubjects || initialSelectedSubjects.length === 0) {
      setSelectedSubjects([]);
    } else {
      setSelectedSubjects(initialSelectedSubjects);
    }
  }, [isOpen, initialSelectedSubjects]);

  const handleChange = item => {
    const isSelected = selectedSubjects.some(i => i.EmpCd === item.EmpCd);
    if (isSelected) {
      // Remove item
      setSelectedSubjects(prev => prev.filter(i => i.EmpCd !== item.EmpCd));
    } else {
      // Add item
      setSelectedSubjects(prev => [...prev, item]);
    }
  };

  const clearSelected = () => {
    setSelectedSubjects([]);
  };

  const onClearAll = () => {
    setSelectedSubjects([]);
    setCompanyCode('');
    setDepartment('');
    setLocation('');
    setManager('');
    setClassification('');
    setEmployees([]);
  };

  useEffect(() => {
    setCompanyCode('');
    setDepartment('');
    setLocation('');
    setManager('');
    setClassification('');
  }, [isOpen]);

  const { data: departmentList } = useQuery({
    queryKey: ['departmentList'],
    queryFn: () => fetchHRCodeList({ type: 'DEPT_ACTIVE' }),
    enabled: true,
    keepPreviousData: true,
  });

  const { mutate: fetchEmployees, isPending } = useMutation({
    mutationFn: fetchEmployeeList,
    onSuccess: res => {
      setEmployees(res);
    },
    onError: error => {
      toast.error(error?.message || 'Failed to fetch employees.', {
        className: 'toast-error',
      });
      setEmployees([]);
    },
  });

  const getEmployeeList = () => {
    const payload = {
      companyCode,
      department,
      location: location?.value ?? '',
      manager: manager?.value ?? '',
      classification: classification?.value ?? '',
    };
    fetchEmployees(payload);
  };

  const onSelectAllChange = checked => {
    if (checked) {
      const activeEmployees = employees.filter(item => item.Active === 'Y');
      setSelectedSubjects(activeEmployees);
    } else {
      setSelectedSubjects([]);
    }
  };

  const getImage = item => {
    if (item) {
      return ImageFileUtil.getEmployeeFilePath(item?.Imagefile);
    }
    return null;
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => toggle(selectedSubjects)}
      className={`modal-subject ${!isFullScreen ? 'modal-collapse-width' : ''}`}
      backdrop={false}
      fullscreen={isFullScreen}
    >
      <ModalHeader
        style={{ fontSize: '18px', fontWeight: '600' }}
        toggle={() => toggle(selectedSubjects)}
      >
        Select Subject
        <Button
          onClick={() => setIsFullScreen(!isFullScreen)}
          className="no-outline"
          outline
        >
          <img src={isFullScreen ? collapse : expand} alt="" />
        </Button>
      </ModalHeader>
      <ModalBody style={{ fontSize: '14px', fontWeight: '400' }}>
        <div className="filter-box">
          <div>
            <strong>Filter By</strong>
          </div>
          <div className="input-items">
            <Row>
              <Col md={4}>
                <Label>
                  Company <span className="text-danger"> *</span>
                </Label>
                <Input
                  type="select"
                  value={companyCode}
                  onChange={event => setCompanyCode(event.target.value.trim())}
                  className="external-filter-input"
                >
                  <option value="">All</option>
                  {companyList?.map(type => (
                    <option key={type.Code} value={type.Code}>
                      {type.Description}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={4}>
                <Label>
                  Department <span className="text-danger"> *</span>
                </Label>
                <Input
                  type="select"
                  className="external-filter-input"
                  onChange={event => setDepartment(event.target.value.trim())}
                >
                  <option value="">Select</option>
                  {departmentList?.map(type => (
                    <option key={type.Code} value={type.Code}>
                      {type.Description}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={4}>
                <Label>
                  Location <span className="text-danger"> *</span>
                </Label>
                <AsyncAutocompleteSelect
                  autoCompleteFn={input =>
                    fetchEmployeeLocation({
                      prefixText: input,
                    })
                  }
                  mappedFn={rawData =>
                    rawData.map(item => ({
                      value: item.Cd,
                      label: item.Des,
                    }))
                  }
                  onChange={selectedOption => setLocation(selectedOption)}
                  value={location}
                  placeholder="Select"
                  isClearable
                  displayOption="bothCustom"
                  inputWidth="100%"
                  inputIcon={
                    <Icon path={mdiAccountOutline} size={0.8} color="#667085" />
                  }
                  defaultShowMenu
                />
              </Col>
            </Row>
            <Row className="input-action-items">
              <div className="inputs">
                <Col>
                  <Label>Manager</Label>
                  <UserAsyncAutocompleteSelect
                    autoCompleteFn={input =>
                      fetchDelegateApprovalAutoComplete({
                        prefixText: input,
                      })
                    }
                    mappedFn={rawData =>
                      rawData.map(item => ({
                        value: item.Cd,
                        label: item.Name,
                        name: item.Name,
                        code: item.Cd,
                        designation: item.Designation,
                        image: item?.Imagefile,
                        coCd: item?.CoCd?.trim(),
                      }))
                    }
                    onChange={selectedOption => setManager(selectedOption)}
                    placeholder="Select"
                    isClearable
                    isSearchable
                    value={manager}
                    inputIcon={
                      <Icon
                        path={mdiAccountOutline}
                        size={0.8}
                        color="#667085"
                      />
                    }
                    rightKeyAssessor="code"
                  />
                </Col>
                <Col>
                  <Label>Classification</Label>
                  <AsyncAutocompleteSelect
                    autoCompleteFn={input =>
                      fetchClassification({
                        prefixText: input,
                      })
                    }
                    mappedFn={rawData =>
                      rawData.map(item => ({
                        value: item.Cd,
                        label: item.Des,
                      }))
                    }
                    onChange={selectedOption =>
                      setClassification(selectedOption)
                    }
                    value={classification}
                    placeholder="Select"
                    isClearable
                    displayOption="bothCustom"
                    inputWidth="100%"
                    inputIcon={
                      <Icon
                        path={mdiAccountOutline}
                        size={0.8}
                        color="#667085"
                      />
                    }
                  />
                </Col>
              </div>
              <div className="action-items">
                <Button
                  onClick={onClearAll}
                  className="btn btn-clear"
                  disabled={isPending}
                  outline
                >
                  Clear
                </Button>
                <Button
                  onClick={() => getEmployeeList()}
                  className="btn btn-show"
                  outline
                  disabled={isPending}
                >
                  Show
                </Button>
              </div>
            </Row>
          </div>
        </div>
        <div className="select-all">
          <Input
            type="checkbox"
            onChange={e => onSelectAllChange(e.target.checked)}
          />
          <Label check>
            <strong>Select All ({Object.keys(selectedSubjects).length})</strong>
          </Label>
        </div>
        <div className="select-items">
          {isPending && (
            <div className="d-flex flex-row flex-wrap justify-content-center align-items-center w-100">
              <Spinner color="primary" />
            </div>
          )}
          {!isPending &&
            (employees.length > 0 ? employees : selectedSubjects)?.map(item => (
              <div
                key={item.EmpCd}
                className={`item ${isFullScreen ? 'item-fullscreen' : ''} ${selectedSubjects.some(i => i.EmpCd === item.EmpCd) ? 'selected' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => handleChange(item)}
              >
                <Input
                  type="checkbox"
                  className="blue-checkbox"
                  checked={selectedSubjects.some(i => i.EmpCd === item.EmpCd)}
                />
                <Avatar
                  name={item.Name || 'Unknown'}
                  size="32px"
                  color="#CFCBDC"
                  fgColor="#475467"
                  maxInitials={2}
                  style={{
                    fontSize: '14px',
                    fontWeight: '600',
                  }}
                  src={getImage(item)}
                  round
                />
                <div className="info">
                  <div className="title">{item.EmpCd}</div>
                  <div className="sub-title-subject-name">
                    <strong>{item.Name}</strong>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="action-items">
          <Button className="btn custom-light-btn" onClick={clearSelected}>
            Clear Selected
          </Button>
          <Button
            className="btn custom-btn"
            disabled={Object.keys(selectedSubjects).length === 0}
            onClick={() => {
              toggle(selectedSubjects);
              togglePeople(peoples, selectedSubjects, true, true);
            }}
            style={{
              backgroundColor: '#017EBD',
              border: '#017EBD',
            }}
          >
            Done
          </Button>
          <div className="selected-info">
            <strong>
              Subjects Selected ({Object.keys(selectedSubjects).length})
            </strong>
          </div>
        </div>
        <div className="selected-subjects">
          {selectedSubjects?.map(item => (
            <Chip
              style={{ margin: '1px' }}
              variant="outlined"
              color="primary"
              onDelete={() => handleChange(item)}
              label={`${item?.EmpCd}: ${item?.Name}`}
            />
          ))}
        </div>
      </ModalFooter>
    </Modal>
  );
}

export default Subject;
